import { computed, inject, Injectable, signal } from '@angular/core';
import { debugLog } from '../../helpers/debugLog';
import type {
  CachedPaymentVerdict,
  PaymentGateState,
  PaymentReason,
} from '../../models/payment-status';
import { BookAnalysisService } from '../readium/book-analysis';
import { ReaderApi } from '../reader-api/reader-api';
import { MetadataService } from '../metadata/metadata';

/** Only a confirmed payment is ever stored, so anything else is stale or tampered with. */
function isPaidVerdict(value: unknown): boolean {
  return typeof value === 'object' && value !== null && Reflect.get(value, 'canContinue') === true;
}

/**
 * Single decision point for the PPU reading limit.
 *
 * Calling the server endpoint *performs* the licence payment, so it must happen exactly once per
 * loan: both verdicts are terminal and persisted, and concurrent callers share one request.
 */
@Injectable({
  providedIn: 'root',
})
export class PaymentStatusService {
  private static readonly THRESHOLD_PERCENTAGE = 10;
  /** Keeps the recomputed boundary locator itself from reading as "past the threshold". */
  private static readonly PROGRESS_TOLERANCE = 0.01;
  private static readonly CACHE_PREFIX = 'ppuPaymentStatus:';

  private readonly readerApi = inject(ReaderApi);
  private readonly metadataService = inject(MetadataService);
  private readonly bookAnalysisService = inject(BookAnalysisService);

  private readonly state = signal<PaymentGateState>('idle');
  private readonly reason = signal<PaymentReason | null>(null);
  private readonly hydratedFor = signal<string | undefined>(undefined);

  private inFlight: Promise<boolean> | undefined;
  // Set once the server could not reach a verdict: the reader is let through and not asked again,
  // because asking is what performs the payment. Kept in memory and never persisted, so reopening
  // the resource asks the OTK afresh in case it is back up.
  private askedAndUnresolved = false;

  readonly gateState = this.state.asReadonly();
  readonly paymentReason = this.reason.asReadonly();
  readonly isChecking = computed(() => this.state() === 'checking');
  readonly isBlocked = computed(() => this.state() === 'blocked');
  readonly thresholdLocator = computed(() =>
    this.bookAnalysisService.getLocatorForPercentage(PaymentStatusService.THRESHOLD_PERCENTAGE),
  );

  /**
   * Synchronous verdict for the reactive guard. A position past the boundary may only be shown
   * once the payment is *confirmed* — asking whether a decision is still needed would stop
   * withholding anything the moment a refusal arrived.
   */
  mustWithhold(rawProgress: number): boolean {
    if (this.isExempt()) return false;
    this.hydrateFromCache();
    if (!PaymentStatusService.exceedsThreshold(rawProgress)) return false;
    return this.state() !== 'allowed';
  }

  // eslint-disable-next-line @typescript-eslint/class-methods-use-this
  exceedsThreshold(rawProgress: number): boolean {
    return PaymentStatusService.exceedsThreshold(rawProgress);
  }

  /** The one async decision point. Resolves to whether the reader may pass the threshold. */
  async authorize(rawProgress: number): Promise<boolean> {
    if (this.isExempt()) return true;
    this.hydrateFromCache();
    if (!PaymentStatusService.exceedsThreshold(rawProgress)) return true;
    if (this.hasVerdict()) return this.state() === 'allowed';

    this.inFlight ??= this.requestStatus();
    return this.inFlight;
  }

  /** Restores a payment already confirmed for this loan, so a reload never charges again. */
  private hydrateFromCache(): void {
    const checkoutId = this.metadataService.checkoutId();
    if (!checkoutId) return;
    if (this.hydratedFor() === checkoutId && this.hasVerdict()) return;

    this.hydratedFor.set(checkoutId);
    if (PaymentStatusService.isPaidCached(checkoutId)) this.state.set('allowed');
  }

  /** Preview never holds a loan, and asking would perform a payment. */
  private isExempt(): boolean {
    if (this.metadataService.isPreviewMode() || this.askedAndUnresolved) return true;

    if (!this.metadataService.checkoutId()) {
      // Silent otherwise, and the reason is almost never the reader: config.checkoutId is added
      // by the Nubereader server, so a backend without that change disables the gate entirely.
      debugLog('PPU gate inactive: the metadata carries no config.checkoutId');
      return true;
    }

    return false;
  }

  private hasVerdict(): boolean {
    return this.state() === 'allowed' || this.state() === 'blocked';
  }

  private async requestStatus(): Promise<boolean> {
    this.state.set('checking');
    try {
      const { canContinue, paymentReason, degraded } = await this.readerApi.getPaymentStatus();
      this.reason.set(paymentReason);

      if (degraded === true) {
        // The server never reached the OTK, so no payment was attempted.
        this.askedAndUnresolved = true;
        this.state.set('idle');
        this.inFlight = undefined;
        return true;
      }

      this.state.set(canContinue ? 'allowed' : 'blocked');
      if (canContinue) this.persistPaid();
      return canContinue;
    } catch (error) {
      debugLog('paymentStatus request failed; letting the active loan continue', error);
      this.askedAndUnresolved = true;
      this.state.set('idle');
      this.inFlight = undefined;
      return true;
    }
  }

  private persistPaid(): void {
    const checkoutId = this.metadataService.checkoutId();
    if (!checkoutId) return;
    try {
      localStorage.setItem(
        `${PaymentStatusService.CACHE_PREFIX}${checkoutId}`,
        JSON.stringify({ canContinue: true, paymentReason: null } satisfies CachedPaymentVerdict),
      );
    } catch (error) {
      debugLog('could not persist the PPU payment verdict', error);
    }
  }

  private static isPaidCached(checkoutId: string): boolean {
    try {
      const raw = localStorage.getItem(`${PaymentStatusService.CACHE_PREFIX}${checkoutId}`);
      if (raw === null) return false;
      return isPaidVerdict(JSON.parse(raw));
    } catch (error) {
      debugLog('could not read the PPU payment verdict cache', error);
      return false;
    }
  }

  private static exceedsThreshold(rawProgress: number): boolean {
    return (
      rawProgress - PaymentStatusService.THRESHOLD_PERCENTAGE >
      PaymentStatusService.PROGRESS_TOLERANCE
    );
  }
}
